/**
 * Text Panel Component for Reels video editor
 * Add and edit text overlays with animations
 */

import React, { useState, useCallback } from 'react';
import { Type, Plus, AlignLeft, AlignCenter, AlignRight, Bold, Italic, Trash2 } from 'lucide-react';
import { useProject, useUI } from '../stores/projectStore';
import { TEXT_ANIMATIONS } from '../utils/effectPresets';

const FONT_FAMILIES = [
  'Arial',
  'Helvetica',
  'Georgia',
  'Times New Roman',
  'Verdana',
  'Courier New',
  'Impact',
  'Comic Sans MS',
  'Trebuchet MS',
  'Palatino Linotype',
];

const FONT_SIZES = [12, 14, 16, 18, 20, 24, 28, 32, 36, 42, 48, 56, 64, 72, 96, 128];

const TextPanel = () => {
  const { project, actions } = useProject();
  const { ui, actions: uiActions } = useUI();
  const [presetCategory, setPresetCategory] = useState('simple');

  // Get selected text clip
  const selectedText = project.textClips.find(t => t.id === ui.selectedTextId);

  // Add new text
  const handleAddText = useCallback((preset = null) => {
    const newText = {
      id: `text-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      text: preset?.text || 'Double-click to edit',
      startTime: ui.currentTime,
      duration: 5,
      position: { x: 0.5, y: 0.5 },
      fontFamily: preset?.fontFamily || 'Arial',
      fontSize: preset?.fontSize || 48,
      fontWeight: preset?.fontWeight || 'bold',
      fontStyle: preset?.fontStyle || 'normal',
      color: preset?.color || '#ffffff',
      backgroundColor: preset?.backgroundColor || 'transparent',
      textAlign: 'center',
      strokeColor: preset?.strokeColor || '#000000',
      strokeWidth: preset?.strokeWidth || 2,
      rotation: 0,
      opacity: 1,
      animation: {
        entrance: preset?.entranceAnimation || 'fade-in',
        emphasis: 'none',
        exit: 'fade-out',
      },
    };

    actions.addTextClip(newText);
    uiActions.setSelectedText(newText.id);
  }, [ui.currentTime, actions, uiActions]);

  // Update text property
  const updateText = useCallback((updates) => {
    if (!selectedText) return;
    actions.updateTextClip(selectedText.id, updates);
  }, [selectedText, actions]);

  // Delete text
  const handleDeleteText = useCallback(() => {
    if (!selectedText) return;
    actions.removeTextClip(selectedText.id);
    uiActions.setSelectedText(null);
  }, [selectedText, actions, uiActions]);

  // Text presets
  const TEXT_PRESETS = {
    simple: [
      { label: 'Heading', text: 'Your Title Here', fontSize: 64, fontWeight: 'bold' },
      { label: 'Subheading', text: 'Subtitle text', fontSize: 36, fontWeight: 'normal' },
      { label: 'Body', text: 'Add your text here', fontSize: 24, fontWeight: 'normal' },
      { label: 'Caption', text: 'Caption text', fontSize: 18, fontWeight: 'normal' },
    ],
    styled: [
      { label: 'Neon', text: 'NEON', fontSize: 72, fontWeight: 'bold', color: '#ff00ff', strokeColor: '#00ffff', strokeWidth: 3 },
      { label: 'Outlined', text: 'OUTLINED', fontSize: 64, fontWeight: 'bold', color: 'transparent', strokeColor: '#ffffff', strokeWidth: 3 },
      { label: 'Shadow', text: 'SHADOW', fontSize: 64, fontWeight: 'bold', color: '#ffffff', backgroundColor: 'rgba(0,0,0,0.5)' },
      { label: 'Gradient', text: 'GRADIENT', fontSize: 64, fontWeight: 'bold', color: '#00d4ff' },
    ],
    animated: [
      { label: 'Typewriter', text: 'Typewriter Effect', fontSize: 48, entranceAnimation: 'typewriter' },
      { label: 'Bounce', text: 'Bouncy!', fontSize: 64, fontWeight: 'bold', entranceAnimation: 'bounce' },
      { label: 'Slide Up', text: 'Slide Up', fontSize: 48, entranceAnimation: 'slide-up' },
      { label: 'Pop', text: 'POP!', fontSize: 72, fontWeight: 'bold', entranceAnimation: 'pop' },
    ],
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-slate-800">
        <h3 className="text-sm font-semibold text-white mb-3">Text & Titles</h3>

        {/* Add text button */}
        <button
          onClick={() => handleAddText()}
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-500 text-white rounded-lg font-medium transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Text
        </button>
      </div>

      {selectedText ? (
        /* Text editor */
        <div className="flex-1 overflow-auto p-4 space-y-4">
          {/* Text content */}
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Text Content</label>
            <textarea
              value={selectedText.text}
              onChange={(e) => updateText({ text: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-teal-500 resize-none"
            />
          </div>

          {/* Font family & size */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Font</label>
              <select
                value={selectedText.fontFamily}
                onChange={(e) => updateText({ fontFamily: e.target.value })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
              >
                {FONT_FAMILIES.map(font => (
                  <option key={font} value={font}>{font}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Size</label>
              <select
                value={selectedText.fontSize}
                onChange={(e) => updateText({ fontSize: Number(e.target.value) })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
              >
                {FONT_SIZES.map(size => (
                  <option key={size} value={size}>{size}px</option>
                ))}
              </select>
            </div>
          </div>

          {/* Font style */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => updateText({ fontWeight: selectedText.fontWeight === 'bold' ? 'normal' : 'bold' })}
              className={`p-2 rounded ${selectedText.fontWeight === 'bold' ? 'bg-teal-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
            >
              <Bold className="w-4 h-4" />
            </button>
            <button
              onClick={() => updateText({ fontStyle: selectedText.fontStyle === 'italic' ? 'normal' : 'italic' })}
              className={`p-2 rounded ${selectedText.fontStyle === 'italic' ? 'bg-teal-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
            >
              <Italic className="w-4 h-4" />
            </button>
            <div className="h-6 w-px bg-slate-700 mx-1" />
            <button
              onClick={() => updateText({ textAlign: 'left' })}
              className={`p-2 rounded ${selectedText.textAlign === 'left' ? 'bg-teal-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
            >
              <AlignLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => updateText({ textAlign: 'center' })}
              className={`p-2 rounded ${selectedText.textAlign === 'center' ? 'bg-teal-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
            >
              <AlignCenter className="w-4 h-4" />
            </button>
            <button
              onClick={() => updateText({ textAlign: 'right' })}
              className={`p-2 rounded ${selectedText.textAlign === 'right' ? 'bg-teal-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
            >
              <AlignRight className="w-4 h-4" />
            </button>
          </div>

          {/* Colors */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Text Color</label>
              <input
                type="color"
                value={selectedText.color}
                onChange={(e) => updateText({ color: e.target.value })}
                className="w-full h-10 rounded-lg cursor-pointer bg-transparent"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Outline Color</label>
              <input
                type="color"
                value={selectedText.strokeColor}
                onChange={(e) => updateText({ strokeColor: e.target.value })}
                className="w-full h-10 rounded-lg cursor-pointer bg-transparent"
              />
            </div>
          </div>

          {/* Outline width */}
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Outline Width</label>
            <input
              type="range"
              min="0"
              max="10"
              step="0.5"
              value={selectedText.strokeWidth}
              onChange={(e) => updateText({ strokeWidth: Number(e.target.value) })}
              className="w-full"
            />
            <div className="flex justify-between text-xs text-slate-500">
              <span>None</span>
              <span>{selectedText.strokeWidth}px</span>
            </div>
          </div>

          {/* Animation */}
          <div>
            <label className="text-xs text-slate-400 mb-2 block">Entrance Animation</label>
            <div className="grid grid-cols-3 gap-2">
              {TEXT_ANIMATIONS.entrance.map((anim) => (
                <button
                  key={anim.id}
                  onClick={() => updateText({ animation: { ...selectedText.animation, entrance: anim.id } })}
                  className={`px-2 py-1.5 text-xs rounded ${selectedText.animation?.entrance === anim.id ? 'bg-teal-600 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'}`}
                >
                  {anim.name}
                </button>
              ))}
            </div>
          </div>

          {/* Timing */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Start Time (s)</label>
              <input
                type="number"
                min="0"
                step="0.1"
                value={selectedText.startTime}
                onChange={(e) => updateText({ startTime: Number(e.target.value) })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
              />
            </div>
            <div>
              <label className="text-xs text-slate-400 mb-1 block">Duration (s)</label>
              <input
                type="number"
                min="0.5"
                step="0.5"
                value={selectedText.duration}
                onChange={(e) => updateText({ duration: Number(e.target.value) })}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-teal-500"
              />
            </div>
          </div>

          {/* Delete button */}
          <button
            onClick={handleDeleteText}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg text-sm transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            Delete Text
          </button>
        </div>
      ) : (
        /* Text presets */
        <div className="flex-1 overflow-auto p-4">
          {/* Category tabs */}
          <div className="flex gap-1 mb-4">
            {Object.keys(TEXT_PRESETS).map((cat) => (
              <button
                key={cat}
                onClick={() => setPresetCategory(cat)}
                className={`px-3 py-1 text-xs rounded-full transition-colors capitalize ${presetCategory === cat ? 'bg-teal-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Preset list */}
          <div className="space-y-2">
            {TEXT_PRESETS[presetCategory].map((preset, idx) => (
              <button
                key={idx}
                onClick={() => handleAddText(preset)}
                className="w-full p-3 bg-slate-800 hover:bg-slate-700 rounded-lg text-left transition-colors group"
              >
                <div
                  className="text-lg mb-1 truncate"
                  style={{
                    fontFamily: preset.fontFamily || 'Arial',
                    fontWeight: preset.fontWeight || 'normal',
                    color: preset.color || '#ffffff',
                    WebkitTextStroke: preset.strokeWidth ? `${preset.strokeWidth}px ${preset.strokeColor}` : undefined,
                  }}
                >
                  {preset.text}
                </div>
                <div className="text-xs text-slate-500">{preset.label}</div>
              </button>
            ))}
          </div>

          {/* Existing text clips */}
          {project.textClips.length > 0 && (
            <div className="mt-6">
              <h4 className="text-xs text-slate-400 mb-2">On Timeline</h4>
              <div className="space-y-1">
                {project.textClips.map((text) => (
                  <button
                    key={text.id}
                    onClick={() => uiActions.setSelectedText(text.id)}
                    className={`w-full p-2 rounded-lg text-left transition-colors ${ui.selectedTextId === text.id ? 'bg-teal-600' : 'bg-slate-800 hover:bg-slate-700'}`}
                  >
                    <div className="text-sm text-white truncate">{text.text}</div>
                    <div className="text-xs text-slate-400">
                      {text.startTime.toFixed(1)}s - {(text.startTime + text.duration).toFixed(1)}s
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TextPanel;
